import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { resolveFormattingLocale } from '@eis/core';
import { DetailsSection } from '@eis/patterns';
import { ConflictNotice } from '../../policies/ui/ConflictNotice';
import { useAcceptLead, useRefreshLead } from '../api/leads';
import { isMemberLead, moneyLabel, tarifNameOf } from '../model/leads';
import { pinStaleDetailOf, refusalMessageOf } from '../model/refusals';
import type { Lead } from '../model/types';
import { useLeadPremiumEstimate } from '../../premium/api/premium';
import { PremiumEstimate } from '../../premium/ui/PremiumEstimate';

/**
 * Accept (SDD-013 §7.2, §8.1.3): the lead becomes a policy in one service transaction. The
 * dialog asks for the optional policy number and sends; every refusal comes back with its reason.
 *
 * LEAD_PIN_STALE (D-L3) is the one refusal with a decision attached: the tariff moved since the
 * offer, and the operator chooses between re-rating the lead (a new version at today's price) and
 * creating the policy at the offered price (the fresh configuration, the lead's pin stated as
 * stale). Both prices are shown, from the typed detail the service attached — never parsed out
 * of a sentence.
 */
export function AcceptLeadDialog({
  lead,
  open,
  onClose,
  onAccepted,
}: {
  readonly lead: Lead;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAccepted: (policyCode: string) => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const accept = useAcceptLead();
  const refresh = useRefreshLead();
  const [policyCode, setPolicyCode] = useState('');
  const code = lead.code ?? '';
  const stale = pinStaleDetailOf(accept.error);
  // The lead's estimate (SDD-021 §9, U-4): the refresh preview the Prämie section shows — the same
  // query, so the dialog and the page show the same numbers.
  const estimate = useLeadPremiumEstimate(code, { enabled: open && stale !== undefined });

  const close = (): void => {
    setPolicyCode('');
    accept.reset();
    refresh.reset();
    onClose();
  };

  const run = (acceptStalePin: boolean): void => {
    accept.mutate(
      {
        code,
        ...(policyCode.trim() === '' ? {} : { policyCode: policyCode.trim() }),
        ...(acceptStalePin ? { acceptStalePin: true } : {}),
        ...(isMemberLead(lead) ? { parentPolicyCode: lead.draft?.parentPolicyCode ?? '' } : {}),
      },
      {
        onSuccess: (response) => {
          onAccepted(response.policy?.code ?? '');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('leads.accept.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('leads.accept.description')}
          </Typography>

          <ConflictNotice conflict={accept.conflict ?? refresh.conflict} />

          {(lead.partnerDrafts ?? []).length > 0 && (
            // SDD-014 PS-5: a draft no longer blocks the accept — the service registers it in
            // the Partner domain first, in its own write, and the policy names the minted code.
            <Alert severity="info" data-testid="registers-drafts">
              {t('leads.accept.registersDrafts', { count: (lead.partnerDrafts ?? []).length })}
            </Alert>
          )}

          {stale === undefined ? (
            <TextField
              label={t('leads.accept.policyCode')}
              value={policyCode}
              size="small"
              fullWidth
              helperText={t('leads.accept.policyCodeHint')}
              onChange={(event) => {
                setPolicyCode(event.target.value);
              }}
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
            />
          ) : (
            <Stack spacing={2} data-testid="stale-pin">
              <Alert severity="warning">
                <AlertTitle>{t('leads.accept.stalePin.title')}</AlertTitle>
                {t('leads.accept.stalePin.description')}
              </Alert>
              {/* Two prices, two configurations — as NAMES, never as coordinates (D-C). */}
              <DetailsSection
                title={t('leads.accept.stalePin.pinned')}
                fields={[
                  {
                    label: t('leads.overview.premium'),
                    value: moneyLabel(stale.pinnedPremium.monthly, locale),
                  },
                  {
                    label: t('leads.overview.tarif'),
                    value: tarifNameOf(stale.pinned.resolvedTarifVersionErn),
                  },
                ]}
              />
              <DetailsSection
                title={t('leads.accept.stalePin.fresh')}
                fields={[
                  {
                    label: t('leads.overview.premium'),
                    value:
                      stale.refreshedPremium.monthly?.amount === stale.pinnedPremium.monthly?.amount
                        ? `${moneyLabel(stale.refreshedPremium.monthly, locale)} (${t('leads.accept.stalePin.unchanged')})`
                        : moneyLabel(stale.refreshedPremium.monthly, locale),
                  },
                  {
                    label: t('leads.overview.tarif'),
                    value: tarifNameOf(stale.fresh.resolvedTarifVersionErn),
                  },
                ]}
              />
              <PremiumEstimate
                premium={lead.draft?.premium}
                estimate={
                  estimate.isError
                    ? { status: 'failed' }
                    : estimate.data?.estimate === undefined
                      ? { status: 'loading' }
                      : { status: 'ready', estimate: estimate.data.estimate }
                }
                locale={locale}
              />
            </Stack>
          )}

          {accept.isError && stale === undefined && accept.conflict === null && (
            <Alert severity="error" data-testid="accept-refusal">
              {refusalMessageOf(accept.error)}
            </Alert>
          )}
          {refresh.isSuccess && <Alert severity="success">{t('leads.refresh.done')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('leads.actions.cancel')}</Button>
        {stale === undefined ? (
          <Button
            variant="contained"
            disabled={accept.isPending || accept.isSuccess}
            onClick={() => {
              run(false);
            }}
          >
            {t('leads.accept.submit')}
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              disabled={refresh.isPending}
              onClick={() => {
                refresh.mutate({ code }, { onSuccess: close });
              }}
            >
              {t('leads.accept.stalePin.refresh')}
            </Button>
            <Button
              variant="contained"
              disabled={accept.isPending}
              onClick={() => {
                run(true);
              }}
            >
              {t('leads.accept.stalePin.acceptOffered')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
